import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type {
  SysMLModel,
  ParseDiagnostic,
  ParseResult,
  SourceRange,
  ViewDefinition as CoreViewDefinition,
  PartDefinition,
  RequirementDefinition,
  VerificationDefinition,
  ConnectionDefinition,
  AllocationDefinition,
  PortTypeDefinition,
  PortDefinition,
  SysMLElement,
  DiagramProjectionNode,
  DiagramProjectionEdge,
  SimulationConfig,
  SimState,
  SimResult,
  SimTraceEntry,
  SimConstraintResult,
  SimRequirementResult,
  SimScenario,
  SimulationStatus,
} from '@arctek/sysml-types';
import type {
  ValidationRuleReport,
  ValidationSummary,
} from '@arctek/sysml-core';
import type {
  DiagramNode,
  DiagramEdge,
  Viewport,
  CursorPosition,
  ViewDefinition,
} from '../api/types.js';

import type {
  LayoutOptions,
  LayoutResult,
  DiagramProjection,
} from '@arctek/sysml-types';
import {
  formatSysmlSource,
  getFixesForDiagnostic,
  diffModels,
  createSnapshot,
  serializeSnapshot,
  formatDiffSummary,
} from '@arctek/sysml-core';
import type {
  ModelSnapshot,
  SerializedSnapshot,
  ModelDiff,
  DiffSummary,
} from '@arctek/sysml-core';
import { projectSysmlDiagram, projectSequenceDiagrams, projectStateMachineDiagrams, projectActivityDiagrams, projectIBD, getIBDCandidates, projectPackageDiagrams, projectRequirementDiagrams, projectUseCaseDiagrams, projectParametricDiagrams } from '@arctek/sysml-projection';
import type { SequenceProjection, StateMachineProjection, ActivityProjection, IBDProjection, PackageProjection, RequirementProjection, UseCaseProjection, ParametricProjection } from '@arctek/sysml-types';
import type { ParseAndValidateParams } from '../workers/language-service.types.js';
import { PARSE_DEBOUNCE_MS, DEFAULT_NODE_WIDTH, DEFAULT_NODE_HEIGHT } from '../utils/constants.js';
import { LanguageServiceClient } from '../workers/language-service-client.js';

// ELK layout — loaded lazily on first use to avoid blocking module init.
let _computeElkLayout: ((
  projection: DiagramProjection,
  options?: Partial<LayoutOptions>,
) => Promise<LayoutResult>) | null = null;
let _elkLoadAttempted = false;

async function getElkLayout(): Promise<typeof _computeElkLayout> {
  if (_elkLoadAttempted) return _computeElkLayout;
  _elkLoadAttempted = true;
  try {
    const elkModule = await import('@arctek/sysml-projection');
    if ('computeElkLayout' in elkModule && typeof elkModule.computeElkLayout === 'function') {
      _computeElkLayout = elkModule.computeElkLayout as NonNullable<typeof _computeElkLayout>;
    }
  } catch {
    // ELK layout not available — fall back to grid layout.
  }
  return _computeElkLayout;
}

// ---------------------------------------------------------------------------
// ModelerController -- core parse / model / diagram state
//
// Ported from: sysmlModelerStore.ts (Pinia)
// Pattern: Lit ReactiveController (triggers host.requestUpdate on state change)
//
// Uses the real @arctek/sysml-core parser + @arctek/sysml-projection projector.
// The inline regex-based parser has been removed.
// ---------------------------------------------------------------------------

// ── Adapter helpers ─────────────────────────────────────────────────────────
// Bridge @arctek/sysml-projection output → Stencil <sysml-diagram> prop shapes.
// The Stencil component expects DiagramNode with `partDefId` (resolved via a
// separate `partDefinitions` prop) and DiagramEdge with `sourceNodeId`/`targetNodeId`.

const AUTO_LAYOUT_COLS = 3;
const GAP_X = 40;
const GAP_Y = 30;
const OFFSET_X = 30;
const OFFSET_Y = 30;

function projectionNodeToDiagramNode(
  pn: DiagramProjectionNode,
  index: number,
  preservedPositions: Map<string, { x: number; y: number }>,
): DiagramNode {
  const preserved = preservedPositions.get(pn.id);
  const col = index % AUTO_LAYOUT_COLS;
  const row = Math.floor(index / AUTO_LAYOUT_COLS);
  return {
    id: pn.id,
    partDefId: pn.elementId,
    elementType: pn.elementType,
    label: pn.label,
    x: preserved ? preserved.x : OFFSET_X + col * (DEFAULT_NODE_WIDTH + GAP_X),
    y: preserved ? preserved.y : OFFSET_Y + row * (DEFAULT_NODE_HEIGHT + GAP_Y),
    width: DEFAULT_NODE_WIDTH,
    height: DEFAULT_NODE_HEIGHT,
    expanded: false,
    subElementCount: pn.subElementCount,
    connectionCount: pn.connectionCount,
  };
}

/** relTypes that render with dashed lines (non-structural relationships) */
const DASHED_REL_TYPES = new Set([
  'satisfy', 'verify', 'allocate', 'dependency', 'derive', 'import',
]);

function projectionEdgeToDiagramEdge(pe: DiagramProjectionEdge): DiagramEdge {
  const rt = pe.relType?.toLowerCase() ?? '';
  return {
    id: pe.id,
    edgeType: rt === 'composition' ? 'partUsage' : 'relationship',
    relationshipId: pe.relationshipId,
    relType: pe.relType,
    sourceNodeId: pe.fromElementId,
    targetNodeId: pe.toElementId,
    label: pe.label ?? undefined,
    lineStyle: DASHED_REL_TYPES.has(rt) ? 'dashed' : 'solid',
  };
}

// ── Controller ──────────────────────────────────────────────────────────────

export class ModelerController implements ReactiveController {
  private host: ReactiveControllerHost & HTMLElement;

  // ── State ──────────────────────────────────────────────────────────────────

  /** Monotonic counter — incremented on every state change. Used by child
   *  components' `hasChanged` to detect when the controller actually mutated. */
  generation = 0;

  sourceText = '';
  parsedModel: SysMLModel | null = null;
  diagnostics: ParseDiagnostic[] = [];
  isParsing = false;
  parseSuccess = false;
  isDirty = false;

  // Diagram
  diagramNodes: DiagramNode[] = [];
  diagramEdges: DiagramEdge[] = [];

  // Layout
  layoutInProgress = false;
  layoutOptions: LayoutOptions = {
    algorithm: 'layered',
    direction: 'RIGHT',
    nodeSpacing: 40,
    layerSpacing: 60,
    edgeRouting: 'ORTHOGONAL',
  };

  // Sequence projection
  sequenceProjections: SequenceProjection[] = [];
  activeSequenceProjection: SequenceProjection | null = null;

  // State machine projection
  stateMachineProjections: StateMachineProjection[] = [];
  activeStateMachineProjection: StateMachineProjection | null = null;

  // Activity projection
  activityProjections: ActivityProjection[] = [];
  activeActivityProjection: ActivityProjection | null = null;

  // Package projection
  packageProjections: PackageProjection[] = [];
  activePackageProjection: PackageProjection | null = null;

  // Requirement projection
  requirementProjections: RequirementProjection[] = [];
  activeRequirementProjection: RequirementProjection | null = null;

  // Use case projection
  useCaseProjections: UseCaseProjection[] = [];
  activeUseCaseProjection: UseCaseProjection | null = null;

  // Parametric projection
  parametricProjections: ParametricProjection[] = [];
  activeParametricProjection: ParametricProjection | null = null;

  // IBD projection
  ibdPartDefId: string | null = null;
  activeIBDProjection: IBDProjection | null = null;

  // IBD decomposition navigation stack
  decompositionStack: Array<{ partDefId: string; partDefName: string }> = [];

  // BDD scoped decomposition
  bddScopePartDefId: string | null = null;
  bddDecompositionStack: Array<{ partDefId: string; partDefName: string }> = [];

  selectedNodeIds: string[] = [];
  selectedEdgeIds: string[] = [];
  viewport: Viewport = { x: 0, y: 0, zoom: 1 };

  // Views
  activeViewId: string | null = null;
  availableViews: ViewDefinition[] = [];

  // Validation
  validationReports: ValidationRuleReport[] = [];
  validationSummary: ValidationSummary = { error: 0, warning: 0, info: 0, totalFailed: 0 };

  // Cursor
  cursorPosition: CursorPosition = { line: 1, column: 1 };

  // History / Semantic Diff
  /** Model snapshots for semantic diff history (newest first) */
  modelSnapshots: ModelSnapshot[] = [];
  /** Maximum number of model snapshots to retain */
  private static readonly MAX_SNAPSHOTS = 30;
  /** Minimum interval between auto-snapshots (ms) */
  private static readonly SNAPSHOT_DEBOUNCE_MS = 10_000;

  // Simulation
  simulationStatus: SimulationStatus = 'idle';
  simulationState: SimState | null = null;
  simulationResult: SimResult | null = null;
  simulationTraceEntries: SimTraceEntry[] = [];
  simulationConstraintResults: SimConstraintResult[] = [];
  simulationRequirementResults: SimRequirementResult[] = [];
  simulationScenarios: SimScenario[] = [];
  activeSimulationStateId: string | null = null;
  activeSimulationActionId: string | null = null;

  private _simulationEngine: import('@arctek/sysml-core').SimulationEngine | null = null;

  // ── Internal ───────────────────────────────────────────────────────────────

  private _parseGeneration = 0;
  private _parseTimer: ReturnType<typeof setTimeout> | null = null;
  private _preservedPositions = new Map<string, { x: number; y: number }>();
  private _languageService = new LanguageServiceClient();
  /** Cached last projection for re-running layout with new options */
  private _lastProjection: DiagramProjection | null = null;
  /** Accumulated change range for the current debounce window */
  private _pendingChangeRange: ParseAndValidateParams['changeRange'] | undefined;
  private _lastSnapshotTime = 0;

  constructor(host: ReactiveControllerHost & HTMLElement) {
    this.host = host;
    host.addController(this);
  }

  hostConnected(): void {}
  hostDisconnected(): void {
    this._clearParseTimer();
    this._languageService.dispose();
  }

  // ── Computed ───────────────────────────────────────────────────────────────

  get partDefinitions(): PartDefinition[] {
    return this.parsedModel?.allPartDefs ?? [];
  }

  get requirements(): RequirementDefinition[] {
    return this.parsedModel?.allRequirementDefs ?? [];
  }

  get verificationCases(): VerificationDefinition[] {
    return this.parsedModel?.allVerificationDefs ?? [];
  }

  get relationships(): (ConnectionDefinition | AllocationDefinition)[] {
    if (!this.parsedModel) return [];
    return [
      ...this.parsedModel.allConnectionDefs,
      ...this.parsedModel.allAllocationDefs,
    ];
  }

  get ports(): (PortTypeDefinition | PortDefinition)[] {
    if (!this.parsedModel) return [];
    return [
      ...this.parsedModel.allPortDefs,
      ...this.parsedModel.allPorts,
    ];
  }

  get errorCount(): number {
    return this.diagnostics.filter((d) => d.severity === 'error').length;
  }

  get warningCount(): number {
    return this.diagnostics.filter((d) => d.severity === 'warning').length;
  }

  get hasValidModel(): boolean {
    return this.parsedModel !== null && this.errorCount === 0;
  }

  get blockCount(): number {
    return this.parsedModel?.allElements.length ?? 0;
  }

  /**
   * Flat list of all elements in the parsed model.
   * Replaces the old recursive _flatElements() helper now that the real parser
   * provides pre-flattened `allElements`.
   */
  get allElements(): SysMLElement[] {
    return this.parsedModel?.allElements ?? [];
  }

  /**
   * Returns the chain of elements from root package down to the deepest element
   * whose source range contains the current cursor position.
   * Used by the breadcrumbs component to show the cursor's scope path.
   */
  getElementPathAtCursor(): SysMLElement[] {
    if (!this.parsedModel) return [];
    const { line, column } = this.cursorPosition;
    const path: SysMLElement[] = [];

    // Walk the allElements list (sorted broadest→narrowest by source range)
    // and collect elements whose source range contains the cursor.
    const candidates = this.allElements
      .filter((el) => {
        const s = el.source;
        if (!s) return false;
        // Check if cursor is within this element's source range
        if (line < s.startLine || line > s.endLine) return false;
        if (line === s.startLine && column < s.startColumn) return false;
        if (line === s.endLine && column > s.endColumn) return false;
        return true;
      })
      .sort((a, b) => {
        // Sort by range size (smallest = most specific last)
        const sizeA = this._sourceRangeSize(a.source!);
        const sizeB = this._sourceRangeSize(b.source!);
        return sizeB - sizeA; // broadest first
      });

    // Build path: each successive element should be strictly contained in the previous
    for (const el of candidates) {
      if (path.length === 0 || this._isContainedIn(el.source!, path[path.length - 1].source!)) {
        path.push(el);
      }
    }

    return path;
  }

  private _sourceRangeSize(s: SourceRange): number {
    // Approximate: use line span * 1000 + column span for ordering
    return (s.endLine - s.startLine) * 1000 + (s.endColumn - s.startColumn);
  }

  private _isContainedIn(inner: SourceRange, outer: SourceRange): boolean {
    if (inner.startLine < outer.startLine) return false;
    if (inner.endLine > outer.endLine) return false;
    if (inner.startLine === outer.startLine && inner.startColumn < outer.startColumn) return false;
    if (inner.endLine === outer.endLine && inner.endColumn > outer.endColumn) return false;
    // Must be strictly smaller (not the same range)
    return this._sourceRangeSize(inner) < this._sourceRangeSize(outer);
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  updateSourceText(
    text: string,
    changeRange?: ParseAndValidateParams['changeRange'],
  ): void {
    if (text === this.sourceText) return; // prevent render loops
    this.sourceText = text;
    this.isDirty = true;
    this._mergeChangeRange(changeRange);
    this._debouncedParse();
    this._update();
  }

  loadSourceText(text: string): void {
    if (text === this.sourceText) return; // prevent render loops
    this.sourceText = text;
    this.isDirty = false;
    this._parseImmediate();
  }

  handleParsed(result: ParseResult): void {
    this.parsedModel = result.model;
    this.diagnostics = result.diagnostics ?? [];
    this.parseSuccess = result.success;
    this.isParsing = false;
    this.validationReports = [];
    this.validationSummary = { error: 0, warning: 0, info: 0, totalFailed: 0 };
    this._buildDiagram();
    this._extractViews();
    this._update();
  }

  selectView(viewId: string | null): void {
    this.activeViewId = viewId;
    this._buildDiagram();
    this._update();
  }

  selectNode(detail: { id: string } | string): void {
    const id = typeof detail === 'string' ? detail : detail.id;
    this.selectedNodeIds = [id];
    this.selectedEdgeIds = [];
    this._update();
  }

  selectEdge(detail: { id: string } | string): void {
    const id = typeof detail === 'string' ? detail : detail.id;
    this.selectedEdgeIds = [id];
    this.selectedNodeIds = [];
    this._update();
  }

  clearSelection(): void {
    this.selectedNodeIds = [];
    this.selectedEdgeIds = [];
    this._update();
  }

  revealInEditor(range: SourceRange): void {
    this.host.dispatchEvent(
      new CustomEvent('sysml-reveal-range', {
        bubbles: true,
        composed: true,
        detail: { range },
      }),
    );
  }

  /**
   * Replace a source range with new text. Used for in-place editing of elements.
   */
  replaceSourceRange(range: SourceRange, newText: string): void {
    const lines = this.sourceText.split('\n');
    // Convert 1-based line/column to 0-based indices
    const startLine = range.startLine - 1;
    const endLine = range.endLine - 1;
    const startCol = range.startColumn - 1;
    const endCol = range.endColumn - 1;

    if (startLine < 0 || startLine >= lines.length) return;

    const before = lines.slice(0, startLine).join('\n')
      + (startLine > 0 ? '\n' : '')
      + lines[startLine].slice(0, startCol);
    const after = (endLine < lines.length ? lines[endLine].slice(endCol) : '')
      + (endLine + 1 < lines.length ? '\n' + lines.slice(endLine + 1).join('\n') : '');

    this.updateSourceText(before + newText + after);
  }

  /**
   * Delete a source range (the element definition). Cleans up trailing blank lines.
   */
  deleteSourceRange(range: SourceRange): void {
    this.replaceSourceRange(range, '');
  }

  handleCursorChange(pos: CursorPosition): void {
    this.cursorPosition = pos;
  }

  updateNodePosition(detail: { id: string; x: number; y: number }): void {
    const idx = this.diagramNodes.findIndex((n) => n.id === detail.id);
    if (idx === -1) return;

    this._preservedPositions.set(detail.id, { x: detail.x, y: detail.y });

    // Create new array reference so Stencil detects the prop change
    this.diagramNodes = this.diagramNodes.map((n) =>
      n.id === detail.id ? { ...n, x: detail.x, y: detail.y } : n,
    );

    // Clear ELK edge sections for connected edges so they fall back to
    // Manhattan routing and follow the dragged node's new position
    this.diagramEdges = this.diagramEdges.map((e) =>
      e.sourceNodeId === detail.id || e.targetNodeId === detail.id
        ? { ...e, sections: undefined }
        : e,
    );

    this._update();
  }

  updateViewport(vp: Viewport): void {
    this.viewport = vp;
  }

  markClean(): void {
    this.isDirty = false;
    this._update();
  }

  autoLayout(): void {
    this._preservedPositions.clear();
    this._buildDiagram();
    this._update();
  }

  /**
   * Update layout options and re-run ELK layout with new settings.
   */
  updateLayoutOptions(opts: Partial<LayoutOptions>): void {
    this.layoutOptions = { ...this.layoutOptions, ...opts };
    this._preservedPositions.clear();
    this._buildDiagram();
    this._update();
  }

  /**
   * Export the current diagram as SVG or PNG.
   * Serializes the <sysml-diagram> SVG and triggers a download.
   */
  async exportDiagram(format: 'svg' | 'png'): Promise<void> {
    // Find the diagram SVG element in the host's shadow DOM tree
    const diagramEl = this.host.shadowRoot?.querySelector('sysml-diagram')
      ?? this.host.querySelector('sysml-diagram');
    if (!diagramEl) return;

    // Access the SVG inside the Stencil component's shadow DOM
    const svg = diagramEl.shadowRoot?.querySelector('svg')
      ?? diagramEl.querySelector('svg');
    if (!svg) return;

    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);
    const docName = 'diagram';

    if (format === 'svg') {
      const blob = new Blob([svgString], { type: 'image/svg+xml' });
      this._downloadBlob(blob, `${docName}.svg`);
      return;
    }

    // PNG: render SVG to canvas then export
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    try {
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Failed to load SVG image'));
        img.src = url;
      });

      const canvas = document.createElement('canvas');
      // Use 2x resolution for crisp export
      const scale = 2;
      canvas.width = img.naturalWidth * scale;
      canvas.height = img.naturalHeight * scale;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);

      const pngBlob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((b) => resolve(b), 'image/png'),
      );
      if (pngBlob) {
        this._downloadBlob(pngBlob, `${docName}.png`);
      }
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  /** Trigger a browser download for a Blob. */
  private _downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ── Format support ─────────────────────────────────────────────────────────

  /**
   * Format the current source text using the real sysml-core formatter.
   * Returns the formatted text without modifying controller state. The caller
   * is responsible for feeding the result back through `updateSourceText()` or
   * `loadSourceText()` as appropriate.
   */
  formatSource(): string {
    if (!this.sourceText.trim()) return this.sourceText;
    try {
      return formatSysmlSource(this.sourceText);
    } catch {
      // Formatting failure is non-fatal; return unchanged source.
      return this.sourceText;
    }
  }

  /**
   * Format the source and load the result, triggering a reparse.
   */
  formatAndApply(): void {
    const formatted = this.formatSource();
    if (formatted !== this.sourceText) {
      this.sourceText = formatted;
      this.isDirty = true;
      this._parseImmediate();
    }
  }

  // ── Diagnostic fixes ───────────────────────────────────────────────────────

  /**
   * Get quick-fixes for a specific diagnostic.
   */
  getFixesForDiagnostic(diagnostic: ParseDiagnostic) {
    return getFixesForDiagnostic(this.sourceText, diagnostic);
  }

  // ── Internal: parse pipeline ───────────────────────────────────────────────

  /**
   * Merge a new change range into the pending accumulator.
   * Multiple rapid edits within a debounce window are merged into a single
   * encompassing range covering all affected lines.
   */
  private _mergeChangeRange(
    range: ParseAndValidateParams['changeRange'] | undefined,
  ): void {
    if (!range) {
      // If any edit within the debounce window lacks range info, discard
      // the accumulator — the worker will fall back to full parse.
      this._pendingChangeRange = undefined;
      return;
    }
    if (!this._pendingChangeRange) {
      this._pendingChangeRange = { ...range };
      return;
    }
    // Widen the range to cover both the previous and new changes
    const prev = this._pendingChangeRange;
    this._pendingChangeRange = {
      startLine: Math.min(prev.startLine, range.startLine),
      endLine: Math.max(prev.endLine, range.endLine),
      lineDelta: prev.lineDelta + range.lineDelta,
    };
  }

  private _debouncedParse(): void {
    this._clearParseTimer();
    this._parseTimer = setTimeout(() => {
      void this._parseImmediate();
    }, PARSE_DEBOUNCE_MS);
  }

  private async _parseImmediate(): Promise<void> {
    this._clearParseTimer();
    if (!this.sourceText.trim()) {
      this.parsedModel = null;
      this.diagnostics = [];
      this.parseSuccess = false;
      this.isParsing = false;
      this.diagramNodes = [];
      this.diagramEdges = [];
      this.availableViews = [];
      this.validationReports = [];
      this.validationSummary = { error: 0, warning: 0, info: 0, totalFailed: 0 };
      this.activeIBDProjection = null;
      this._update();
      return;
    }

    const generation = ++this._parseGeneration;
    this.isParsing = true;

    // Consume the accumulated change range for this parse cycle
    const changeRange = this._pendingChangeRange;
    this._pendingChangeRange = undefined;

    this._update();

    try {
      const result = await this._languageService.parseAndValidate(
        this.sourceText,
        changeRange,
      );

      if (generation !== this._parseGeneration) return;

      this.parsedModel = result.parseResult.model;
      this.diagnostics = result.parseResult.diagnostics;
      this.parseSuccess = result.parseResult.success;
      this.validationReports = result.validationReports;
      this.validationSummary = result.validationSummary;
    } catch (err: unknown) {
      if (generation !== this._parseGeneration) return;
      this.parsedModel = null;
      this.diagnostics = [
        {
          severity: 'error',
          message: err instanceof Error ? err.message : 'Unknown parse error',
          line: 1,
          column: 1,
        },
      ];
      this.parseSuccess = false;
      this.validationReports = [];
      this.validationSummary = { error: 0, warning: 0, info: 0, totalFailed: 0 };
    } finally {
      // Always clear isParsing for the latest generation — prevents stuck spinner
      // when a stale parse early-returns above
      if (generation !== this._parseGeneration) {
        this._update();
        return;
      }
    }

    this.isParsing = false;
    this._buildDiagram();
    this._buildSequenceProjections();
    this._buildStateMachineProjections();
    this._buildActivityProjections();
    this._buildPackageProjections();
    this._buildRequirementProjections();
    this._buildUseCaseProjections();
    this._buildParametricProjections();
    this._buildIBDProjection();
    this._extractViews();
    this._autoSnapshot();
    this._update();
  }

  private _clearParseTimer(): void {
    if (this._parseTimer !== null) {
      clearTimeout(this._parseTimer);
      this._parseTimer = null;
    }
  }

  // ── Internal: diagram projection ───────────────────────────────────────────

  private _buildDiagram(): void {
    if (!this.parsedModel) {
      this.diagramNodes = [];
      this.diagramEdges = [];
      this._lastProjection = null;
      return;
    }

    try {
      const activeViewName = this._resolveActiveViewName();
      const projection = projectSysmlDiagram(this.parsedModel, {
        view: activeViewName,
        scope: this.bddScopePartDefId ?? undefined,
      });

      // Cache projection for layout re-runs
      this._lastProjection = projection;

      // Build initial grid-positioned nodes
      this.diagramNodes = projection.nodes.map((pn, i) =>
        projectionNodeToDiagramNode(pn, i, this._preservedPositions),
      );
      this.diagramEdges = projection.edges.map(projectionEdgeToDiagramEdge);

      // Run automatic layout asynchronously (ELK loaded lazily on first use).
      if (this._preservedPositions.size === 0) {
        void this._runElkLayout(projection);
      }
    } catch {
      // Projection failure is non-fatal.
    }
  }

  /**
   * Run ELK layout asynchronously and apply results to diagram nodes/edges.
   */
  private async _runElkLayout(projection: DiagramProjection): Promise<void> {
    const elkFn = await getElkLayout();
    if (!elkFn) return;

    this.layoutInProgress = true;
    this._update();

    try {
      const result: LayoutResult = await elkFn(projection, this.layoutOptions);

      // Apply node positions from layout result
      const nodePositionMap = new Map(
        result.nodes.map((ln) => [ln.id, ln]),
      );
      this.diagramNodes = this.diagramNodes.map((dn) => {
        const layoutNode = nodePositionMap.get(dn.id);
        if (layoutNode) {
          return {
            ...dn,
            x: layoutNode.x,
            y: layoutNode.y,
            width: layoutNode.width,
            height: layoutNode.height,
          };
        }
        return dn;
      });

      // Apply edge sections from layout result
      const edgeSectionMap = new Map(
        result.edges.map((le) => [le.id, le.sections]),
      );
      this.diagramEdges = this.diagramEdges.map((de) => {
        const sections = edgeSectionMap.get(de.id) ?? edgeSectionMap.get(de.relationshipId ?? '');
        if (sections) {
          return { ...de, sections };
        }
        return de;
      });
    } catch {
      // ELK failure is non-fatal — grid layout remains.
    } finally {
      this.layoutInProgress = false;
      this._update();
    }
  }

  private _buildSequenceProjections(): void {
    if (!this.parsedModel) {
      this.sequenceProjections = [];
      this.activeSequenceProjection = null;
      return;
    }

    try {
      this.sequenceProjections = projectSequenceDiagrams(this.parsedModel);
      // Auto-select the first projection if available
      this.activeSequenceProjection = this.sequenceProjections[0] ?? null;
    } catch {
      this.sequenceProjections = [];
      this.activeSequenceProjection = null;
    }
  }

  selectSequenceProjection(contextLabel: string): void {
    this.activeSequenceProjection =
      this.sequenceProjections.find(p => p.contextLabel === contextLabel) ?? null;
    this._update();
  }

  selectSequenceProjectionByElementId(elementId: string): boolean {
    const proj = this.sequenceProjections.find(p => p.contextElementId === elementId);
    if (!proj) return false;
    this.activeSequenceProjection = proj;
    this._update();
    return true;
  }

  private _buildStateMachineProjections(): void {
    if (!this.parsedModel) {
      this.stateMachineProjections = [];
      this.activeStateMachineProjection = null;
      return;
    }

    try {
      this.stateMachineProjections = projectStateMachineDiagrams(this.parsedModel);
      this.activeStateMachineProjection = this.stateMachineProjections[0] ?? null;
    } catch {
      this.stateMachineProjections = [];
      this.activeStateMachineProjection = null;
    }
  }

  selectStateMachineProjection(contextLabel: string): void {
    this.activeStateMachineProjection =
      this.stateMachineProjections.find(p => p.contextLabel === contextLabel) ?? null;
    this._update();
  }

  selectStateMachineByElementId(elementId: string): boolean {
    const proj = this.stateMachineProjections.find(p => p.contextElementId === elementId);
    if (!proj) return false;
    this.activeStateMachineProjection = proj;
    this._update();
    return true;
  }

  private _buildActivityProjections(): void {
    if (!this.parsedModel) {
      this.activityProjections = [];
      this.activeActivityProjection = null;
      return;
    }

    try {
      this.activityProjections = projectActivityDiagrams(this.parsedModel);
      this.activeActivityProjection = this.activityProjections[0] ?? null;
    } catch {
      this.activityProjections = [];
      this.activeActivityProjection = null;
    }
  }

  selectActivityProjection(contextLabel: string): void {
    this.activeActivityProjection =
      this.activityProjections.find(p => p.contextLabel === contextLabel) ?? null;
    this._update();
  }

  selectActivityByElementId(elementId: string): boolean {
    const proj = this.activityProjections.find(p => p.contextElementId === elementId);
    if (!proj) return false;
    this.activeActivityProjection = proj;
    this._update();
    return true;
  }

  // ── Package projection ─────────────────────────────────────────────────────

  private _buildPackageProjections(): void {
    if (!this.parsedModel) {
      this.packageProjections = [];
      this.activePackageProjection = null;
      return;
    }

    try {
      this.packageProjections = projectPackageDiagrams(this.parsedModel);
      this.activePackageProjection = this.packageProjections[0] ?? null;
    } catch {
      this.packageProjections = [];
      this.activePackageProjection = null;
    }
  }

  selectPackageProjection(contextLabel: string): void {
    this.activePackageProjection =
      this.packageProjections.find(p => p.contextLabel === contextLabel) ?? null;
    this._update();
  }

  // ── Requirement projection ─────────────────────────────────────────────────

  private _buildRequirementProjections(): void {
    if (!this.parsedModel) {
      this.requirementProjections = [];
      this.activeRequirementProjection = null;
      return;
    }

    try {
      this.requirementProjections = projectRequirementDiagrams(this.parsedModel);
      this.activeRequirementProjection = this.requirementProjections[0] ?? null;
    } catch {
      this.requirementProjections = [];
      this.activeRequirementProjection = null;
    }
  }

  selectRequirementProjection(contextLabel: string): void {
    this.activeRequirementProjection =
      this.requirementProjections.find(p => p.contextLabel === contextLabel) ?? null;
    this._update();
  }

  // ── Use case projection ────────────────────────────────────────────────────

  private _buildUseCaseProjections(): void {
    if (!this.parsedModel) {
      this.useCaseProjections = [];
      this.activeUseCaseProjection = null;
      return;
    }

    try {
      this.useCaseProjections = projectUseCaseDiagrams(this.parsedModel);
      this.activeUseCaseProjection = this.useCaseProjections[0] ?? null;
    } catch {
      this.useCaseProjections = [];
      this.activeUseCaseProjection = null;
    }
  }

  selectUseCaseProjection(contextLabel: string): void {
    this.activeUseCaseProjection =
      this.useCaseProjections.find(p => p.contextLabel === contextLabel) ?? null;
    this._update();
  }

  // ── Parametric projection ──────────────────────────────────────────────────

  private _buildParametricProjections(): void {
    if (!this.parsedModel) {
      this.parametricProjections = [];
      this.activeParametricProjection = null;
      return;
    }

    try {
      this.parametricProjections = projectParametricDiagrams(this.parsedModel);
      this.activeParametricProjection = this.parametricProjections[0] ?? null;
    } catch {
      this.parametricProjections = [];
      this.activeParametricProjection = null;
    }
  }

  selectParametricProjection(contextLabel: string): void {
    this.activeParametricProjection =
      this.parametricProjections.find(p => p.contextLabel === contextLabel) ?? null;
    this._update();
  }

  // ── IBD projection ────────────────────────────────────────────────────────

  /**
   * Select a part definition as the IBD target and build its projection.
   */
  selectIBDTarget(partDefId: string): void {
    this.ibdPartDefId = partDefId;
    this.decompositionStack = [];
    this._buildIBDProjection();
    this._update();
  }

  /**
   * Auto-select the first IBD candidate (part def with sub-parts).
   * Returns true if a candidate was found, false otherwise.
   */
  autoSelectIBDTarget(): boolean {
    if (!this.parsedModel) return false;
    const candidates = getIBDCandidates(this.parsedModel);
    if (candidates.length === 0) return false;
    this.ibdPartDefId = candidates[0].id;
    this.decompositionStack = [];
    this._buildIBDProjection();
    this._update();
    return true;
  }

  /**
   * Drill into a nested part's definition, pushing the current target onto the stack.
   */
  drillIntoIBD(partDefId: string): void {
    if (!this.parsedModel) return;

    // Resolve the part usage to its type definition
    const partUsage = this.parsedModel.allElements.find(e => e.id === partDefId);
    const typeRef = (partUsage as unknown as { typeRef?: string })?.typeRef;
    if (!typeRef || typeRef === '?') return;

    // Find the actual part definition for this type
    const targetDef = this.parsedModel.allPartDefs.find(
      pd => pd.id === typeRef || pd.qualifiedName === typeRef || pd.name === typeRef
    );
    if (!targetDef || targetDef.parts.length === 0) return;

    // Push current onto stack
    if (this.ibdPartDefId) {
      const currentDef = this.parsedModel.allPartDefs.find(pd => pd.id === this.ibdPartDefId);
      if (currentDef) {
        this.decompositionStack = [
          ...this.decompositionStack,
          { partDefId: currentDef.id, partDefName: currentDef.name },
        ];
      }
    }

    this.ibdPartDefId = targetDef.id;
    this._buildIBDProjection();
    this._update();
  }

  /**
   * Navigate up one level in the decomposition stack.
   */
  navigateUpDecomposition(): void {
    if (this.decompositionStack.length === 0) return;
    const parent = this.decompositionStack[this.decompositionStack.length - 1];
    this.decompositionStack = this.decompositionStack.slice(0, -1);
    this.ibdPartDefId = parent.partDefId;
    this._buildIBDProjection();
    this._update();
  }

  /**
   * Jump to a specific level in the decomposition stack.
   */
  navigateToDecompositionLevel(index: number): void {
    if (index < 0 || index >= this.decompositionStack.length) return;
    const target = this.decompositionStack[index];
    this.decompositionStack = this.decompositionStack.slice(0, index);
    this.ibdPartDefId = target.partDefId;
    this._buildIBDProjection();
    this._update();
  }

  /**
   * Returns the full decomposition path for breadcrumb display.
   * Includes the current target as the last item.
   */
  get decompositionPath(): Array<{ partDefId: string; partDefName: string }> {
    const path = [...this.decompositionStack];
    if (this.ibdPartDefId && this.parsedModel) {
      const currentDef = this.parsedModel.allPartDefs.find(pd => pd.id === this.ibdPartDefId);
      if (currentDef) {
        path.push({ partDefId: currentDef.id, partDefName: currentDef.name });
      }
    }
    return path;
  }

  // ── BDD scoped decomposition ─────────────────────────────────────────────

  /**
   * Scope the BDD to show only the children of a specific part definition.
   */
  scopeBDD(partDefId: string): void {
    if (!this.parsedModel) return;

    // Push current scope onto stack (if we're already scoped)
    if (this.bddScopePartDefId) {
      const currentDef = this.parsedModel.allPartDefs.find(pd => pd.id === this.bddScopePartDefId)
        ?? this.parsedModel.allStateDefs.find(sd => sd.id === this.bddScopePartDefId);
      if (currentDef) {
        this.bddDecompositionStack = [
          ...this.bddDecompositionStack,
          { partDefId: currentDef.id, partDefName: currentDef.name },
        ];
      }
    }

    this.bddScopePartDefId = partDefId;
    this._preservedPositions.clear();
    this._buildDiagram();
    this._update();
  }

  /**
   * Navigate up one level in the BDD decomposition stack.
   */
  navigateUpBDD(): void {
    if (this.bddDecompositionStack.length === 0) {
      // At root — clear scope entirely
      this.bddScopePartDefId = null;
      this._preservedPositions.clear();
      this._buildDiagram();
      this._update();
      return;
    }
    const parent = this.bddDecompositionStack[this.bddDecompositionStack.length - 1];
    this.bddDecompositionStack = this.bddDecompositionStack.slice(0, -1);
    this.bddScopePartDefId = parent.partDefId;
    this._preservedPositions.clear();
    this._buildDiagram();
    this._update();
  }

  /**
   * Jump to a specific level in the BDD decomposition stack.
   * Index -1 means the full (unscoped) model.
   */
  navigateToBDDLevel(index: number): void {
    if (index < 0) {
      // Navigate to full model
      this.bddScopePartDefId = null;
      this.bddDecompositionStack = [];
    } else if (index < this.bddDecompositionStack.length) {
      const target = this.bddDecompositionStack[index];
      this.bddDecompositionStack = this.bddDecompositionStack.slice(0, index);
      this.bddScopePartDefId = target.partDefId;
    }
    this._preservedPositions.clear();
    this._buildDiagram();
    this._update();
  }

  /**
   * Returns the full BDD decomposition path for breadcrumb display.
   * Includes the current scope target as the last item.
   * Returns empty array when unscoped (full model view).
   */
  get bddDecompositionPath(): Array<{ partDefId: string; partDefName: string }> {
    if (!this.bddScopePartDefId) return [];
    const path = [...this.bddDecompositionStack];
    if (this.parsedModel) {
      const currentDef = this.parsedModel.allPartDefs.find(pd => pd.id === this.bddScopePartDefId);
      if (currentDef) {
        path.push({ partDefId: currentDef.id, partDefName: currentDef.name });
      }
    }
    return path;
  }

  private _buildIBDProjection(): void {
    if (!this.parsedModel || !this.ibdPartDefId) {
      this.activeIBDProjection = null;
      return;
    }

    try {
      this.activeIBDProjection = projectIBD(this.parsedModel, this.ibdPartDefId);
    } catch {
      this.activeIBDProjection = null;
    }
  }

  /**
   * Resolve the active view id to a view name that projectSysmlDiagram understands.
   */
  private _resolveActiveViewName(): string | undefined {
    if (!this.activeViewId || !this.parsedModel) return undefined;
    const view = this.parsedModel.allViews.find((v) => v.id === this.activeViewId);
    return view?.name ?? view?.qualifiedName ?? undefined;
  }

  private _extractViews(): void {
    if (!this.parsedModel) {
      this.availableViews = [];
      return;
    }
    this.availableViews = this.parsedModel.allViews.map((v: CoreViewDefinition) => ({
      id: v.id,
      name: v.name,
      query: v.uses.join(', '),
    }));
  }

  // ── Snapshot / History ──────────────────────────────────────────────────────

  /**
   * Take a snapshot of the current model state.
   * Auto-called after successful parses (debounced).
   */
  private _autoSnapshot(): void {
    if (!this.parsedModel || !this.parseSuccess) return;

    const now = Date.now();
    if (now - this._lastSnapshotTime < ModelerController.SNAPSHOT_DEBOUNCE_MS) return;

    // Skip if source text hasn't changed since last snapshot
    const lastSnap = this.modelSnapshots[0];
    if (lastSnap && lastSnap.sourceText === this.sourceText) return;

    const snap = createSnapshot(
      this.sourceText,
      this.parsedModel,
      lastSnap,
    );

    this.modelSnapshots = [
      snap,
      ...this.modelSnapshots.slice(0, ModelerController.MAX_SNAPSHOTS - 1),
    ];
    this._lastSnapshotTime = now;
  }

  /**
   * Create a manual checkpoint snapshot with a label.
   */
  createCheckpoint(label: string): ModelSnapshot | null {
    if (!this.parsedModel) return null;

    const lastSnap = this.modelSnapshots[0];
    const snap = createSnapshot(
      this.sourceText,
      this.parsedModel,
      lastSnap,
      label,
    );

    this.modelSnapshots = [
      snap,
      ...this.modelSnapshots.slice(0, ModelerController.MAX_SNAPSHOTS - 1),
    ];
    this._lastSnapshotTime = Date.now();
    this._update();
    return snap;
  }

  /**
   * Compute a diff between two snapshots by index.
   */
  diffSnapshots(baseIndex: number, targetIndex: number): ModelDiff | null {
    const base = this.modelSnapshots[baseIndex];
    const target = this.modelSnapshots[targetIndex];
    if (!base || !target) return null;

    return diffModels(base.model, target.model, {
      baseTimestamp: base.timestamp,
      targetTimestamp: target.timestamp,
    });
  }

  /**
   * Get the diff summary for a snapshot (compared to its predecessor).
   */
  getSnapshotDiffSummary(index: number): string {
    const snap = this.modelSnapshots[index];
    if (!snap?.diffFromPrevious) return 'No previous snapshot';
    return formatDiffSummary(snap.diffFromPrevious.summary);
  }

  /**
   * Restore the model to a specific snapshot.
   */
  restoreSnapshot(index: number): void {
    const snap = this.modelSnapshots[index];
    if (!snap) return;
    this.loadSourceText(snap.sourceText);
  }

  /**
   * Clear all snapshots.
   */
  clearSnapshots(): void {
    this.modelSnapshots = [];
    this._update();
  }

  // ── Simulation ─────────────────────────────────────────────────────────────

  /**
   * Load available simulation scenarios.
   * Called lazily when the simulation panel is first opened.
   */
  async loadSimulationScenarios(): Promise<void> {
    try {
      const { getToasterScenarios } = await import('@arctek/sysml-core');
      this.simulationScenarios = getToasterScenarios();
      this._update();
    } catch {
      // simulation module not available
    }
  }

  /**
   * Start a simulation with the given scenario ID.
   */
  async startSimulation(scenarioId: string): Promise<void> {
    if (!this.parsedModel) return;
    const scenario = this.simulationScenarios.find(s => s.id === scenarioId);
    if (!scenario) return;

    try {
      const { SimulationEngine } = await import('@arctek/sysml-core');
      this._simulationEngine = new SimulationEngine(this.parsedModel);

      // Populate state machine and action IDs from the parsed model
      const config: SimulationConfig = {
        ...scenario.config,
        stateMachineIds: this.parsedModel.allStateDefs.map(sd => sd.id),
        actionIds: scenario.config.actionIds.length > 0
          ? scenario.config.actionIds
          : [],
      };

      const result = this._simulationEngine.run(config);

      this.simulationResult = result;
      this.simulationState = result.finalState;
      this.simulationTraceEntries = result.trace.entries;
      this.simulationConstraintResults = result.constraintResults;
      this.simulationRequirementResults = result.requirementResults;
      this.simulationStatus = 'completed';

      // Find the final active state name for diagram highlighting
      const activeStates = result.finalState.activeStates;
      const firstSmId = Object.keys(activeStates)[0];
      if (firstSmId) {
        const stateDef = this.parsedModel.allStateDefs.find(sd => sd.id === firstSmId);
        if (stateDef?.states) {
          const activeState = stateDef.states.find(s => s.id === activeStates[firstSmId]);
          this.activeSimulationStateId = activeState?.id ?? null;
        }
      }

      this._update();
    } catch (err) {
      this.simulationStatus = 'error';
      this._update();
      console.error('[Simulation]', err);
    }
  }

  /**
   * Reset the simulation state.
   */
  resetSimulation(): void {
    this._simulationEngine = null;
    this.simulationStatus = 'idle';
    this.simulationState = null;
    this.simulationResult = null;
    this.simulationTraceEntries = [];
    this.simulationConstraintResults = [];
    this.simulationRequirementResults = [];
    this.activeSimulationStateId = null;
    this.activeSimulationActionId = null;
    this._update();
  }

  // ── Internal: helpers ──────────────────────────────────────────────────────

  private _update(): void {
    this.generation++;
    this.host.requestUpdate();
  }
}
